import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Series: Menopause — "The symptoms she blamed on aging were estrogen withdrawal all along".

export const menopauseDiane: Scenario = {
  slug: 'menopause-diane',
  series: 'Menopause',
  title: "She thought hiking was over. Her hormones told another story.",
  socialHook:
    "Diane, 56, gave up hiking, watched her mood go flat, and blamed aging. She had avoided HRT for five years because breast cancer ran in her family. Her genetics changed the question: what if the risk she feared was not the whole story, and the symptoms she accepted were not inevitable?",
  meta: {
    description:
      "A fictional case study: Diane is a retired school principal who stopped hiking because of joint pain and has watched her mood go gray since menopause. She avoided HRT for five years because of breast cancer concerns. Her ESR1, COL1A1, and BDNF genetics help explain why estrogen loss hit her joints, bones, and mood harder than expected.",
  },

  persona: {
    name: 'Diane',
    age: 56,
    sex: 'Female',
    ancestry: 'White Canadian',
    occupation: 'Retired school principal',
    chiefConcern:
      'Joint pain severe enough to stop hiking, a flat mood she describes as gray rather than sad, and a dentist who flagged bone density concerns. She went through menopause at 51 and assumed this was normal aging. She has avoided HRT for five years because breast cancer runs in her family.',
    familyHistory: 'Mother: breast cancer at 62. Maternal aunt: breast cancer at 68.',
  },

  clinicalPicture: {
    symptoms: [
      'Persistent joint pain — knees and hips, worse after rest, stopped hiking two years ago',
      'Flat, low-affect mood — describes it as "gray, not sad"; loss of motivation and interest without feeling classically depressed',
      'Mild brain fog — slower to find words, reduced concentration when reading',
      'Dentist-flagged bone density concerns at last check-up',
    ],
    labs: [
      {
        name: 'DXA T-score (lumbar spine)',
        value: '−1.9',
        refRange: 'Above −1.0 (normal); −1.0 to −2.5 (osteopenia); below −2.5 (osteoporosis)',
        flag: 'low',
      },
      {
        name: 'hsCRP (high-sensitivity C-reactive protein)',
        value: '3.4 mg/L',
        refRange: 'Below 1.0 mg/L (low risk); 1.0–3.0 (moderate); above 3.0 (elevated)',
        flag: 'high',
      },
    ],
    medications: [],
    supplements: ['Calcium 500 mg/day (self-prescribed after dentist visit)'],
    lifestyle: [
      'Formerly active — daily hikes, now limited to short flat walks due to joint pain',
      'Non-smoker, occasional alcohol (wine at weekends)',
      'Diet: generally balanced; modest protein, few processed foods',
      'Sleep: adequate duration but unrefreshing; wakes feeling flat',
    ],
  },

  genetics: [
    {
      gene: 'ESR1',
      variant: 'Reduced-sensitivity variant',
      rsid: 'rs2234693',
      genotype: 'Lower-sensitivity allele',
      plainMeaning:
        "The warning Diane heard was broad; her own breast tissue may be less reactive to estrogen signals than average. That does not erase family history, but it makes her personal risk conversation more specific than a simple yes-or-no rule.",
      technicalDetail:
        "ESR1 encodes estrogen receptor alpha (ERα), the primary estrogen receptor expressed in breast tissue. The rs2234693 variant is associated with reduced ERα expression and signaling efficiency in breast epithelial cells. Population-level breast cancer risk estimates derived from family history assume average receptor sensitivity; Diane's variant places her at the lower end of the receptor-sensitivity distribution. The biological mechanism linking estrogen to breast cancer risk depends on receptor activation — lower receptor responsiveness attenuates this pathway in breast tissue specifically, without reducing estrogen's protective role in bone, joints, or the brain, where different receptor subtypes and tissue-specific expression patterns apply.",
    },
    {
      gene: 'COL1A1',
      variant: 'Weaker-collagen variant',
      rsid: 'rs1107946',
      genotype: 'Lower-expression allele',
      plainMeaning:
        "Her joints and bones had less collagen reserve to begin with. When estrogen fell, the tissues that kept her hiking lost support faster than she expected.",
      technicalDetail:
        "COL1A1 encodes the alpha-1 chain of type I collagen, the structural protein that forms the matrix of bone, cartilage, tendons, and ligaments. The rs1107946 variant is associated with reduced COL1A1 transcriptional activity, yielding lower collagen output per cell. Estrogen upregulates COL1A1 expression directly via ERβ signaling in chondrocytes and osteoblasts — it is one of the primary hormonal drivers of collagen turnover and bone matrix maintenance. At menopause, estrogen withdrawal causes a measurable decline in collagen synthesis across the musculoskeletal system. In women with the rs1107946 variant, the baseline is already lower, so the same withdrawal produces a disproportionately large functional gap. This explains both Diane's accelerated cartilage thinning (joint pain out of proportion to her age) and her DXA result.",
    },
    {
      gene: 'BDNF',
      variant: 'Val66Met — reduced secretion',
      rsid: 'rs6265',
      genotype: 'Met allele carrier',
      plainMeaning:
        "The flat, gray feeling has a possible biology behind it. Estrogen normally helps support a brain protein tied to mood and motivation, and Diane's variant leaves her with less backup when estrogen drops.",
      technicalDetail:
        "BDNF (brain-derived neurotrophic factor) supports neuronal survival, plasticity, and the signaling that underlies mood regulation and executive function. The Val66Met variant (rs6265, Met allele) impairs activity-dependent secretion of BDNF — the protein is produced but released less efficiently in response to neuronal activity. Estrogen upregulates BDNF transcription via ERα in the hippocampus and prefrontal cortex; postmenopausal estrogen withdrawal reduces BDNF expression substantially. In a Val66Met carrier, this combines a genetic floor-level reduction in BDNF secretion with the hormone-withdrawal reduction on top. The result is not the acute sadness of a depressive episode but a sustained blunting of motivational tone, affect, and cognitive sharpness — exactly the gray flatness Diane describes, and mechanistically distinct from major depression.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "She stopped hiking and called it aging",
      body: "Diane spent thirty years running a school. She was the person other people came to with problems. She hiked every weekend and planned retirement around longer days on the trails. Then her knees started. Then her hips. Then the mornings got harder, not from lack of sleep but from a gray feeling that had slowly become her baseline. A dentist mentioned bone density. She bought calcium. She told herself this was what getting older looked like. After her mother's breast cancer diagnosis, she had already decided HRT was not for her.",
    },
    {
      type: 'narrative',
      heading: "The decision made sense. The symptoms still needed an explanation.",
      body: "When Diane's mother was diagnosed with breast cancer in her early sixties, Diane heard a simple message: family history raises risk, so HRT is unsafe. She kept that rule for five years and treated her joint pain and low mood as the cost of aging. But estrogen does more than act on breast tissue. It helps maintain joint cartilage, supports bone renewal, and keeps mood-related brain pathways active. The hot flashes stopped. The estrogen withdrawal kept showing up elsewhere.",
    },
    {
      type: 'reveal',
      heading: "The risk story was more personal than the rule she was given",
      body: "Diane did not need a blanket yes or no. She needed a better risk conversation. Her ESR1 result suggests her breast tissue may respond less strongly to estrogen signals than average, even though her family history still deserves attention. At the same time, the tissues causing her daily problems — joints, bone, and brain — rely on estrogen in different ways. The clue was not that estrogen was simply good or bad. It was that the same hormone could carry different meanings in different tissues.",
    },
    {
      type: 'reveal',
      heading: "Her joints and mood had less backup than she thought",
      body: "Diane's cartilage had less margin before menopause ever arrived. Her COL1A1 variant points to lower collagen output, so when estrogen stopped helping collagen synthesis, her joints and bones felt the drop sooner. Her mood followed a similar pattern. The BDNF variant means her brain had less reserve in a pathway tied to motivation and mental sharpness. The flatness was not a new personality or early dementia. It was a clue that estrogen withdrawal had been affecting more than hot flashes.",
    },
    {
      type: 'action',
      heading: "Four conversations worth having now",
      items: [
        "Book a consultation with a menopause specialist and bring the ESR1 context. The question is not simply 'family history means no HRT'; it is how Diane's personal risk profile compares with the symptoms and bone changes she is living with now.",
        "Ask about low-dose transdermal estrogen. A patch or gel bypasses liver metabolism and has a lower clotting and systemic risk profile than oral HRT. The goal is steady support for joints, bone, and brain pathways without the peaks of oral preparations.",
        "Start a structured joint-support protocol ahead of the specialist appointment: collagen peptides (10g daily), vitamin D tested and dosed to target a level above the minimum threshold, and a resistance training program designed for joint rehabilitation. These work with — not instead of — HRT review, and they address the COL1A1 gap directly.",
        "Request a formal bone density scan if the dentist's concern was informal. The DXA result provides a precise baseline, and at Diane's score she's in a range where the trajectory over the next few years will depend heavily on what she does now. Knowing the number makes the decision concrete.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'streak' as const,
      heading: "Diane's 8-week goal: book the visit, rebuild the trail habit",
      goalLabel: 'Menopause specialist appointment + daily joint-support protocol',
      caption:
        "Diane had put off seeing a menopause specialist because she thought her symptoms were just aging. Once the pattern made sense, she set two goals: book the appointment and start a daily joint-support routine with collagen peptides, vitamin D, and short resistance sessions.",
      weeks: [
        {
          weekLabel: 'Week 1',
          days: [true, false, false, true, true, false, true],
          note: "Booked the specialist appointment for week six — first time I've actually done it rather than thinking about it. Protocol started: collagen peptides and vitamin D with breakfast. Four days.",
        },
        {
          weekLabel: 'Week 2',
          days: [true, true, false, true, true, true, false],
          note: "Five days. Missed Wednesday and Sunday — weekend routine still loose. Added a short resistance session on Tuesday; knees ached afterwards but in a worked feeling rather than a bad feeling.",
        },
        {
          weekLabel: 'Week 3',
          days: [true, true, true, false, true, true, true],
          note: "Six days. First week I've managed resistance training twice. Woke up three mornings this week and noticed I didn't immediately feel gray — hard to describe, just slightly more present.",
        },
        {
          weekLabel: 'Week 4',
          days: [true, true, true, true, false, true, true],
          note: "Six days again. Missed Friday — long day and forgot — but made up for it Saturday. Morning stiffness in my knees is fractionally shorter. Joint protocol feels automatic now.",
        },
        {
          weekLabel: 'Week 5',
          days: [true, true, true, true, true, true, false],
          note: "Six days. Best week yet for consistency. Walked a longer flat route on Thursday — first time I've pushed the distance in over a year. Mood noticeably more even across the week.",
        },
        {
          weekLabel: 'Week 6',
          days: [true, true, true, true, true, true, true],
          note: "Specialist appointment this week. First perfect week on the protocol — felt important to show up having actually done the work. Specialist took the ESR1 context seriously. Discussing low-dose transdermal.",
        },
        {
          weekLabel: 'Week 7',
          days: [true, true, true, true, true, false, true],
          note: "Six days. Resistance training three times this week — a new high. The specialist appointment shifted something; I feel like I'm making decisions rather than just managing symptoms.",
        },
        {
          weekLabel: 'Week 8',
          days: [true, true, true, true, true, true, true],
          note: "Perfect week. Transdermal estrogen patch starts next week. Hiked a gentle trail on Sunday for the first time in two years — shorter than the old routes, but on the trail again.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'menopause',
    label: 'Explore your menopause genetics in Coach',
  },
}
